import { type FC, useEffect, useState } from "react";
import Modal from "../../common/components/Modal";
import { useTranslation, type WithTranslation } from "react-i18next";
import useMediaQuery from "../../common/hooks/useMediaQuery";
import { type PublicationDownloadFile } from "../../models/ProductData";
import {
  getPublicationsDownloadsLanguages,
  makePublicationsDropdownEntriesLanguages,
} from "./PdpTabs/PublicationsDownloadTab";
import { SimpleSelect } from "../../common/components/Select/SimpleSelect";
import { containsHTML, removeSpecificTag } from "../../utils/string";
import Parameter from "../../common/components/Parameter";
import { IconType } from "../../common/components/icon/IconType";
import { Link } from "../../common/components/Link";
import { trackPublicationDownload, trackPublicationOrder } from "../../feature/Analytics/ga/ga";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "../../utils/screenSizes";
import { Button } from "~/common/components/Button/Button";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Stacker } from "~/common/components/Stacker/Stacker";

interface Props {
  publications: PublicationDownloadFile[];
  defaultLanguage?: string;
  close: () => void;
}

const PublicationPopup: FC<Props> = ({ close, publications, defaultLanguage }) => {
  const { t } = useTranslation();
  // get available languages from publications list
  const languages = getPublicationsDownloadsLanguages(publications);
  const languageCode = getDefaultLanguageCodeBasedOnPublicationsSite(defaultLanguage, publications, t);
  const [currentLanguage, setCurrentLanguage] = useState<string>(languageCode);

  // get the current publication based on current/default language
  const [loadedPublication, setLoadedPublication] = useState<PublicationDownloadFile>();
  const loadPublication = () => {
    const publication = publications.find((publicationDoc) => publicationDoc.languageCode === currentLanguage);
    if (!publication) {
      return;
    }
    setLoadedPublication(publication);
  };
  const dropdownLanguages = makePublicationsDropdownEntriesLanguages(languages, t);

  // detect fields change
  useEffect(loadPublication, [currentLanguage]);

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  const title = <h5>{loadedPublication?.titleRaw}</h5>;
  const subTitle = <h6>{loadedPublication?.subline}</h6>;
  const cover = loadedPublication
    ? loadedPublication.image.trim() !== "" && <img src={loadedPublication.image} alt={loadedPublication.shortTitle} />
    : "";
  const disableLanguage = dropdownLanguages.length === 1;
  const languageSelector = (
    <SimpleSelect
      label={t("productDetailsPage.tabs.downloads.popup.language")}
      values={dropdownLanguages}
      fullWidth={true}
      onChange={(e) => setCurrentLanguage(e.target.value as string)}
      defaultValue={dropdownLanguages.length === 1 ? dropdownLanguages[0].value : currentLanguage}
      disabled={disableLanguage}
    />
  );
  const description = <PublicationDescriptionBlock publication={loadedPublication} />;

  const footer = <PublicationFooter close={close} publication={loadedPublication} />;

  return (
    <Modal onClose={close} footerChildren={footer} modalClassName={"scha__modal__publication-download"}>
      <div className={"scha__publication-modal__container" + (!isDesktop ? " mobile" : "")}>
        <div className="row">
          <div className="scha__publication-modal__container__sidebar col-4">
            <div className="scha__publication-modal__container__sidebar__cover">{cover}</div>
            <div className="scha__publication-modal__container__sidebar__language">{languageSelector}</div>
          </div>
          <div className="scha__publication-modal__container__content col-8">
            <div className="scha__publication-modal__container__content__title">{title}</div>
            <div className="scha__publication-modal__container__content__subtitle">{subTitle}</div>
            <div className="scha__publication-modal__container__content__cover">{cover}</div>
            <div className="scha__publication-modal__container__content__language">{languageSelector}</div>
            <div className="scha__publication-modal__container__content__description">{description}</div>
            <div className="scha__publication-modal__container__content__meta">
              {loadedPublication && (
                <div className="row">
                  <div className="col-md-6">
                    <Parameter
                      label={t("productDetailsPage.tabs.downloads.popup.meta.mediaCategory")}
                      value={loadedPublication.mediaCategory}
                    />
                    <Parameter
                      label={t("productDetailsPage.tabs.downloads.popup.meta.date")}
                      value={loadedPublication.year}
                    />
                    <Parameter
                      label={t("productDetailsPage.tabs.downloads.popup.meta.publisher")}
                      value={loadedPublication.editor}
                    />
                  </div>
                  <div className="col-md-6">
                    <Parameter
                      label={t("productDetailsPage.tabs.downloads.popup.meta.pages")}
                      value={loadedPublication.pages}
                    />
                    <Parameter
                      label={t("productDetailsPage.tabs.downloads.popup.meta.language")}
                      value={loadedPublication.language}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

interface PublicationFooterProps {
  publication?: PublicationDownloadFile;
  close: () => void;
}
const PublicationFooter: FC<PublicationFooterProps> = ({ close, publication }) => {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);
  return (
    <Stacker
      gap={16}
      className={`d-flex ${
        isDesktop ? "flex-direction-row flex-justify-between flex-align-center" : "flex-direction-column-reverse"
      } w-100`}
    >
      <Link className={isDesktop ? "" : "m-auto"} onClick={close}>
        {t("productDetailsPage.tabs.downloads.popup.footer.close")}
      </Link>

      <Stacker direction={isDesktop ? "row" : "column"} gap={8}>
        {publication?.isorderable && !publication.iswebshop && publication.url.trim().length >= 8 && (
          <Button
            href={publication.url}
            target={"_blank"}
            className={"action-order"}
            color={"secondary"}
            startIcon={<DefaultButtonIcon type={IconType.Pages} />}
            onClick={() => publication && trackPublicationOrder(publication)}
            data-test-id={"btn-product-details-publication-download-popup-order"}
          >
            {t("productDetailsPage.tabs.downloads.popup.footer.order")}
          </Button>
        )}

        <Button
          component="a"
          href={publication?.href}
          target={"_blank"}
          className={"action-download"}
          startIcon={<DefaultButtonIcon type={IconType.Download} />}
          onClick={() => publication && trackPublicationDownload(publication)}
          data-test-id={"btn-product-details-publication-download-popup-download"}
        >
          {t("productDetailsPage.tabs.downloads.popup.footer.download") + " (" + publication?.size + ")"}
        </Button>
      </Stacker>
    </Stacker>
  );
};

interface PublicationDescriptionBlockProps {
  publication?: PublicationDownloadFile;
}
const PublicationDescriptionBlock: FC<PublicationDescriptionBlockProps> = ({ publication }) => {
  if (!publication || !publication.description || publication.description.trim() === "") {
    return null;
  }
  // xss protection - remove script tags, just in case
  const text = removeSpecificTag(publication.description.trim(), "script");
  const hasHTML = containsHTML(text);
  // has html and the first char is a tag starts with html
  if (hasHTML && text.indexOf("<") === 0) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: text,
        }}
      />
    );
  }
  // doesn't start with paragraph, but contains html
  if (hasHTML) {
    return (
      <p
        dangerouslySetInnerHTML={{
          __html: text,
        }}
      />
    );
  }
  // is just text
  return <p>{text}</p>;
};

export default PublicationPopup;

const getDefaultLanguageCodeBasedOnPublicationsSite = (
  defaultCode: string | undefined,
  publications: PublicationDownloadFile[],
  t: WithTranslation["t"],
): string => {
  // if default code is available (sent from parent) then use that as default
  if (!!defaultCode && typeof defaultCode === "string" && defaultCode.trim().length > 0) {
    return defaultCode;
  }

  // check for publication list
  if (publications.length === 0) {
    return defaultCode || "";
  }

  // grab site language
  const siteLanguage = window.SCHA.config.language;

  // find a document in site language and extract the language code
  const publicationInSiteLanguage = publications.find(
    (publication) => publication.languageKey.toLowerCase() === siteLanguage.toLowerCase(),
  );
  // if found then return language code based on site language
  if (publicationInSiteLanguage) {
    return publicationInSiteLanguage.languageCode;
  }

  const availableSiteLanguages = [...window.SCHA.config.availableLanguages.map((lang) => lang.toLowerCase())];
  // Additional checking DUE "English" and "English US" language codes in publications
  // check for EN in site and !US, then add to available list the US
  if (availableSiteLanguages.indexOf("en") > -1 && availableSiteLanguages.indexOf("us") === -1) {
    availableSiteLanguages.push("us");
  }
  // check for US in site and !EN, then add to available list the EN
  if (availableSiteLanguages.indexOf("us") > -1 && availableSiteLanguages.indexOf("en") === -1) {
    availableSiteLanguages.push("en");
  }
  // find a document in available site language and extract the language code
  const publicationInAvailableSiteLanguage = publications.filter(
    (publication) => availableSiteLanguages.indexOf(publication.languageKey.toLowerCase()) > -1,
  );
  if (publicationInAvailableSiteLanguage.length > 0) {
    publicationInAvailableSiteLanguage.sort((a, b) => a.languageKey.localeCompare(b.languageKey));
    return publicationInAvailableSiteLanguage[0].languageCode;
  }
  // !!! LAST acceptable option to return a valid language code for as default value
  // grab all the languages from the publications
  const availablePublicationsLanguageCodes = publications.map((publication) => publication.languageCode);
  const sortedPublications = makePublicationsDropdownEntriesLanguages(availablePublicationsLanguageCodes, t);
  return sortedPublications[0].value;
};
